import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  when,
} from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { viewportObserver } from '@nova/utils'
import '@vendor/media-chrome'
import '@vendor/media-chrome-vimeo'
import '@vendor/media-chrome-youtube'

class NovaAdaptiveVideo extends LitElement {
  static properties = {
    sectionId: {
      type: String,
      attribute: 'section-id',
    },
    srcDesktop: {
      type: String,
      attribute: 'src-desktop',
    },
    aspectRatioDesktop: {
      type: String,
      attribute: 'aspect-ratio-desktop',
    },
    srcMobile: {
      type: String,
      attribute: 'src-mobile',
    },
    aspectRatioMobile: {
      type: String,
      attribute: 'aspect-ratio-mobile',
    },
    srcType: {
      type: String,
      attribute: 'src-type',
    },
    srcTypeDesktop: {
      type: String,
      attribute: 'src-type-desktop',
    },
    srcTypeMobile: {
      type: String,
      attribute: 'src-type-mobile',
    },
    posterDesktop: {
      type: String,
      attribute: 'poster-desktop',
    },
    posterMobile: {
      type: String,
      attribute: 'poster-mobile',
    },
    descriptionDesktop: {
      type: String,
      attribute: 'description-desktop',
    },
    descriptionMobile: {
      type: String,
      attribute: 'description-mobile',
    },
    loadingType: {
      type: String,
      attribute: 'loading',
    },
    viewport: {
      type: String,
      state: true,
    },
    autoplay: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    sound: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    captions: {
      type: String,
      attribute: 'captions',
    },
    trackProgress: {
      type: Boolean,
      attribute: 'track-progress',
    },
    background: {
      type: Boolean,
      attribute: 'background',
    },
    adaptContainerHeight: {
      type: Boolean,
      attribute: 'adapt-container-height',
    },
    breakPoint: {
      type: Number,
      attribute: 'break-point',
      reflect: true,
    },
    alwaysShowControls: {
      type: Boolean,
      attribute: 'always-show-controls',
    },
  }

  get isMobileViewport() {
    if (typeof window === 'undefined') return false
    return window.innerWidth < this.breakPoint
  }

  get activeSrc() {
    // Always fallback on desktop asset if no mobile asset is provided
    if (!this.srcMobile) return this.srcDesktop
    // Return mobile asset if current viewport width is less than breakPoint, else return desktop asset
    return this.isMobileViewport
      ? this.srcMobile
      : this.srcDesktop
  }

  get activeAspectRatio() {
    // Always fallback on desktop asset if no mobile asset is provided
    if (!this.aspectRatioMobile) return this.aspectRatioDesktop
    // Return mobile asset if current viewport width is less than breakPoint, else return desktop asset
    return this.isMobileViewport
      ? this.aspectRatioMobile
      : this.aspectRatioDesktop
  }

  get activeDesc() {
    return this.activeSrc === this.srcMobile &&
      this.descriptionMobile !== 'false'
      ? this.descriptionMobile
      : this.activeSrc === this.srcDesktop &&
          this.descriptionDesktop !== 'false'
        ? this.descriptionDesktop
        : ''
  }

  get activePoster() {
    return this.activeSrc === this.srcMobile && this.posterMobile !== 'false'
      ? this.posterMobile
      : this.activeSrc === this.srcDesktop && this.posterDesktop !== 'false'
        ? this.posterDesktop
        : false
  }

  get activeSrcType() {
    if (this.srcTypeMobile && this.srcTypeDesktop) {
      return this.isMobileViewport ? this.srcTypeMobile : this.srcTypeDesktop
    }
    if (this.srcTypeMobile && !this.srcTypeDesktop) {
      return this.isMobileViewport ? this.srcTypeMobile : this.srcType
    }
    if (this.srcTypeDesktop && !this.srcTypeMobile) {
      return this.isMobileViewport ? this.srcType : this.srcTypeDesktop
    }
    return this.srcType
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.srcMobile = false
    this.srcDesktop = false
    this.aspectRatioMobile = false
    this.aspectRatioDesktop = '16/9'
    this.videoRef = createRef()
    this.loadingType = 'lazy'
    this.background = false
    this.adaptContainerHeight = false
    this.breakPoint = 768
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.hasAttribute('aspect-ratio-desktop')) {
      this.aspectRatioDesktop = this.getAttribute('aspect-ratio-desktop')
    }
    if (this.hasAttribute('aspect-ratio-mobile')) {
      this.aspectRatioMobile = this.getAttribute('aspect-ratio-mobile')
    }
    if (this.hasAttribute('break-point')) {
      const breakPointValue = this.getAttribute('break-point')
      this.breakPoint = breakPointValue ? Number(breakPointValue) : 768
    }

    this._updateAspectRatioCSS()

    if (this.autoplay) this.autoplaying = true
    this.percentageCompleted = 0
    this.videoTwentyFive = false
    this.videoFifty = false
    this.videoSeventyFive = false
    this.videoComplete = false
    this.videoInView = false

    if (this.hasAttribute('always-show-controls')) {
      this.alwaysShowControls = this.hasAttribute('always-show-controls')
    }

    this._handleResize = () => {
      this.requestUpdate()
    }
    window.addEventListener('resize', this._handleResize)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._handleResize) {
      window.removeEventListener('resize', this._handleResize)
    }
  }

  firstUpdated() {
    this.$video = this.videoRef.value
    this._updateAspectRatioCSS()
    this._updateAutohide()
    this._preventVideoClick()
  }

  _preventVideoClick() {
    const mediaController = this.shadowRoot?.querySelector('media-controller')
    if (mediaController) {
      const handleClick = (e) => {
        const target = e.target
        const isControlBar = target.closest('media-control-bar')
        const isVideoElement = target.matches('video, youtube-video, vimeo-video') ||
                              target.closest('video, youtube-video, vimeo-video')

        if (isVideoElement && !isControlBar) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
      mediaController.addEventListener('click', handleClick, true)
    } else {
      requestAnimationFrame(() => this._preventVideoClick())
    }
  }


  _updateAspectRatioCSS() {
    const aspectRatio = this.activeAspectRatio || this.aspectRatioDesktop || '16/9'
    this.style.setProperty('--active-aspect-ratio', aspectRatio)
    const mediaController = this.shadowRoot?.querySelector('media-controller')
    if (mediaController) {
      mediaController.style.setProperty('--active-aspect-ratio', aspectRatio)
    }
  }

  _updateAutohide() {
    if (!this.alwaysShowControls) return

    requestAnimationFrame(() => {
      const mediaController = this.shadowRoot?.querySelector('media-controller')
      if (mediaController) {
        mediaController.setAttribute('autohide', '-1')
      } else {
        requestAnimationFrame(() => this._updateAutohide())
      }
    })
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
  }

  _handlePlay = () => {
    if (!this.autoplaying) {
      // Track play analytics event here
    }
  }

  _handlePause = () => {
    this.autoplaying = false
    // Track pause analytics event here
  }

  _handleProgress = () => {
    if (this.$video?.readyState >= 2 && this.trackProgress) {
      const progressUpdate = () => {
        this.percentageCompleted =
          (this.$video.currentTime / this.$video.duration) * 100
        if (!this.videoTwentyFive && this.percentageCompleted > 25) {
          this.videoTwentyFive = true
          this._handleTrackProgress('25%')
        }
        if (!this.videoFifty && this.percentageCompleted > 50) {
          this.videoFifty = true
          this._handleTrackProgress('50%')
        }
        if (!this.videoSeventyFive && this.percentageCompleted > 75) {
          this.videoSeventyFive = true
          this._handleTrackProgress('75%')
        }
        if (!this.videoComplete && this.percentageCompleted > 99) {
          this.videoComplete = true
          this._handleTrackProgress('100%')
          this.$video.removeEventListener('timeupdate', progressUpdate)
        }
      }
      this.$video.addEventListener('timeupdate', progressUpdate)
    }
  }

  _handleTrackProgress = (progress) => {
    // Track progress analytics event here
  }

  renderYoutubeVideo = () => {
    return html`
      <youtube-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions}
      </youtube-video>
    `
  }

  renderVimeoVideo = () => {
    return html`
      <vimeo-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions}
      </vimeo-video>
    `
  }

  renderUploadedVideo = () => {
    return html`
      <video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions}
      </video>
    `
  }

  renderCaptions = () => {
    if (this.captions) {
      return html`
        <track
          label="English"
          kind="subtitles"
          srclang="en"
          src="${window.__DNA_GLOBAL__?.domain}/cdn/shop/files/${
            this.captions.split('files/')[2]
          }"
        />`
    }
  }

  renderControls = () => {
    return html`
      <media-control-bar part="controls">
        <media-play-button>
          <span slot="play">${paths.play}</span>
          <span slot="pause">${paths.pause}</span>
        </media-play-button>
        ${when(
          this.sound,
          () => html`
            <media-mute-button>
              <span slot="high">${paths.audioOn}</span>
              <span slot="off">${paths.audioOff}</span>
            </media-mute-button>
          `,
          () => nothing,
        )}
        ${when(
          this.captions,
          () => html`
            <media-captions-button>
              <span slot="on">${paths.captionsOn}</span>
              <span slot="off">${paths.captionsOff}</span>
            </media-captions-button>
          `,
          () => nothing,
        )}
      </media-control-bar>
    `
  }

  play() {
    this.$video.play()
  }

  render() {
    const stateClasses = {
      'background-video': this.background,
      'adapter-height': this.adaptContainerHeight,
    }

    return html`
      <style>
        @media screen and (max-width: ${this.breakPoint}px) {
          media-controller.background-video {
            height: 100%;
            width: 100%;
            left: unset;
            transform: unset;
          }
          media-controller.background-video #video-element {
            object-fit: cover;
            width: 100%;
            height: 100%;
          }
        }
        media-controller media-play-button {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media screen and (min-width: ${this.breakPoint}px) {
          #video-element {
            object-fit: cover;
          }
          media-controller media-play-button {
            width: 45px;
            height: 45px;
          }
          media-controller media-control-bar {
            padding: 20px 24px;
          }
        }
        ${this.alwaysShowControls
          ? `
          media-controller media-control-bar {
            opacity: 1 !important;
          }
          media-controller[userinactive] media-control-bar {
            opacity: 1 !important;
          }
        `
          : ''}
      </style>

      <media-controller
        class="${classMap(stateClasses)}"
        part="media-controller"
        style="--active-aspect-ratio: ${this.activeAspectRatio || '16/9'}"
        no-click-to-play>
        ${when(
          this.activeSrcType === 'youtube',
          this.renderYoutubeVideo,
          () => nothing,
        )}
        ${when(this.activeSrcType === 'vimeo', this.renderVimeoVideo, () => nothing)}
        ${when(
          this.activeSrcType === 'upload',
          this.renderUploadedVideo,
          () => nothing,
        )}
        ${when(
          this.activePoster,
          () => html`
              <media-poster-image
                slot="poster"
                src=${this.activePoster}>
              </media-poster-image>`,
          () => nothing,
        )}
        ${when(this.controls, this.renderControls, () => nothing)}
      </media-controller>
    `
  }

  willUpdate(changedProperties) {
    if (
      changedProperties.has('aspectRatioDesktop') ||
      changedProperties.has('aspectRatioMobile') ||
      changedProperties.has('breakPoint')
    ) {
      this._updateAspectRatioCSS()
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (changedProperties.has('alwaysShowControls') || changedProperties.has('controls')) {
      this._updateAutohide()
    }
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      :host {
        display: block;
        height: 100%;
        width: 100%;
        --active-aspect-ratio: 16/9;
      }
      #video-element {
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
      media-poster-image::part(poster img) {
        object-fit: cover;
      }
      media-controller {
        width: 100%;
        height: auto;
        aspect-ratio: var(--active-aspect-ratio, 16/9);
        display: block;
        background-color: transparent;
      }
      .adapter-height {
        width: auto;
        height: 100%;
        left: 50%;
        transform: translateX(-50%);
      }
      media-control-bar {
        padding: 5px 0;
        gap: var(--nova-space-2xs);
      }
      media-control-bar > * {
        cursor: pointer;
        --media-control-background: transparent;
        --media-control-hover-background: transparent;
        line-height: 0;
        margin-right: calc(var(--nova-space-2xs) - 1px);
        padding: 0;
      }
      media-control-bar > *:last-child {
        margin: 0;
      }
    `,
  ]
}

customElements.define('nova-adaptive-video', NovaAdaptiveVideo)
